import { useApi } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader } from "@/components/common/PageHeader";
import { KpiTile } from "@/components/common/KpiTile";
import { LoadingCards, LoadingBlock, ErrorState } from "@/components/common/StateViews";
import { SeverityChip } from "@/components/common/SeverityChip";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarCard, AreaCard } from "@/components/common/Charts";
import { ChartInsight } from "@/components/common/ChartInsight";
import { fmtCurrency, fmtNum, fmtPct, VENDOR_LABEL, timeAgo } from "@/lib/format";
import { Link } from "react-router-dom";

export default function Cockpit() {
  const { scopeDept } = useApp();
  const { data, isLoading, error } = useApi(["cockpit", scopeDept], "/cockpit/summary", { dept: scopeDept });
  if (error) return <ErrorState />;

  const k = data?.kpis;
  return (
    <div>
      <PageHeader eyebrow="Navitra Lens · Cockpit" title="Command Cockpit" subtitle="One view of spend, risk, adoption and savings across every AI vendor — last 30 days.">
        <Button asChild variant="outline" size="sm"><Link to="/admin/policies" data-testid="quick-policy">Simulate a policy</Link></Button>
        <Button asChild size="sm"><Link to="/admin/hub" data-testid="quick-connector">Add connector</Link></Button>
      </PageHeader>

      {isLoading || !k ? <LoadingCards /> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiTile testid="spend" accent label="Spend vs budget" value={fmtCurrency(k.spend.value)} sub={`${fmtPct(k.spend.pct)} of ${fmtCurrency(k.spend.budget)} budget`} delta={fmtPct(k.spend.pct)} deltaDir="up"
            spark={data.spend_trend.map((d) => d.usd)}
            explain={{ description: "Total AI spend for the last 30 days.", formula: "token_spend (metered) + seat_spend (assigned × monthly price)", inputs: [{ label: "Token spend", value: fmtCurrency(k.spend.tokens) }, { label: "Seat spend", value: fmtCurrency(k.spend.seats) }, { label: "Budget", value: fmtCurrency(k.spend.budget) }], sources: [{ id: "CP-08", name: "OpenAI admin usage" }, { id: "CP-06", name: "Claude analytics" }, { id: "CP-01", name: "Entra licences" }] }} />
          <KpiTile testid="active-users" label="Weekly active users" value={fmtNum(k.active_users.value)} sub={`of ${k.active_users.licensed} licensed`}
            explain={{ formula: "distinct users with ≥ 1 AI interaction in 30 days", inputs: [{ label: "Active", value: k.active_users.value }, { label: "Licensed", value: k.active_users.licensed }], sources: [{ id: "CP-01", name: "Entra ID identity" }] }} />
          <KpiTile testid="blocks" label="Blocked events" value={fmtNum(k.blocks.value)} sub={`across ${fmtNum(k.blocks.events)} sampled events`}
            explain={{ formula: "policy decision = block", inputs: [{ label: "Blocks", value: k.blocks.value }, { label: "Events", value: k.blocks.events }], sources: [{ id: "CP-05", name: "Defender shadow-AI" }, { id: "Gate", name: "Navitra Gateway" }] }} />
          <KpiTile testid="savings" label="Identified savings" value={fmtCurrency(k.savings.value)} sub="optimisation opportunities"
            explain={{ formula: "Σ estimated_saving over open recommendations R01–R12", inputs: [{ label: "Accepted", value: fmtCurrency(k.savings.accepted) }], sources: [{ id: "Ledger", name: "Savings ledger" }] }} />
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4 mt-4">
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle className="font-display text-lg">Spend trend (30 days)</CardTitle></CardHeader>
          <CardContent>{isLoading ? <LoadingBlock /> : (
            <ChartInsight title="Spend trend (30 days)" app="Command Cockpit"
              context={`Daily AI spend over the last 30 days, totalling ${fmtCurrency(k.spend.value)} (${fmtPct(k.spend.pct)} of budget).`}
              data={(data.spend_trend || []).map((d) => ({ label: d.date, usd: d.usd }))}>
              <AreaCard data={data.spend_trend} xKey="date" yKey="usd" fmt={(v) => fmtCurrency(v)} />
            </ChartInsight>
          )}</CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="font-display text-lg">Spend by vendor</CardTitle></CardHeader>
          <CardContent>
            {isLoading ? <LoadingBlock /> : (
              <ChartInsight title="Spend by vendor" app="Command Cockpit"
                context={`How AI spend splits across vendors; ${VENDOR_LABEL[data.spend_by_vendor[0]?.vendor] || "the top vendor"} leads.`}
                data={data.spend_by_vendor.map((v) => ({ label: VENDOR_LABEL[v.vendor] || v.vendor, usd: v.usd }))}>
                <div className="space-y-3">
                  {data.spend_by_vendor.map((v) => {
                    const max = data.spend_by_vendor[0].usd;
                    return (
                      <div key={v.vendor}>
                        <div className="flex justify-between text-sm mb-1"><span>{VENDOR_LABEL[v.vendor]}</span><span className="mono tabular-nums">{fmtCurrency(v.usd)}</span></div>
                        <div className="h-2 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full bg-navy" style={{ width: `${(v.usd / max) * 100}%` }} /></div>
                      </div>
                    );
                  })}
                </div>
              </ChartInsight>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="mt-4">
        <CardHeader><CardTitle className="font-display text-lg">Needs attention</CardTitle></CardHeader>
        <CardContent>
          {isLoading ? <LoadingBlock className="h-48" /> : (
            <Table>
              <TableHeader><TableRow><TableHead>Incident</TableHead><TableHead>Severity</TableHead><TableHead>Status</TableHead><TableHead>Owner</TableHead><TableHead>Age</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.needs_attention.map((i) => (
                  <TableRow key={i.id} data-testid={`attention-row-${i.id}`}>
                    <TableCell className="font-medium max-w-xs">{i.title}</TableCell>
                    <TableCell><SeverityChip level={i.severity} dot /></TableCell>
                    <TableCell><SeverityChip level={i.status} /></TableCell>
                    <TableCell className="text-muted-foreground">{i.assignee}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{timeAgo(i.created)}</TableCell>
                    <TableCell className="text-right"><Button asChild variant="ghost" size="sm"><Link to="/admin/risk">Triage</Link></Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
